import { deleteApp, getApps, initializeApp } from "firebase/app";
import {
    OAuthProvider,
    deleteUser,
    getAdditionalUserInfo,
    getAuth,
    signInWithPopup,
    signOut
} from "firebase/auth";

import { firebaseConfig } from "../constants/constants";

const CAPTURE_APP_NAME = "mba-directory-capture";

/**
 * "Capture from sign-in" (tenant_authentication_plan.md §2.6): run a
 * Microsoft popup against the TARGET tenant on a secondary Firebase app, so
 * the super admin's own session is untouched, and read the Entra directory
 * id (`tid`) from Microsoft's ID token. Nothing is verified here — the id
 * only fills a form field; real sign-ins are verified by the backend.
 *
 * A Firebase account the popup created for the occasion is deleted again;
 * one that already existed in the tenant is left alone.
 */
export async function captureMicrosoftDirectoryId(tenantId: string): Promise<string> {
    const app =
        getApps().find(a => a.name === CAPTURE_APP_NAME) ??
        initializeApp(firebaseConfig, CAPTURE_APP_NAME);
    const auth = getAuth(app);
    auth.tenantId = tenantId;
    try {
        const result = await signInWithPopup(auth, new OAuthProvider("microsoft.com"));
        const idToken = OAuthProvider.credentialFromResult(result)?.idToken;
        const tid = idToken ? decodeTid(idToken) : "";
        if (!tid) {
            throw new Error("Microsoft did not return a directory id for this account.");
        }
        if (getAdditionalUserInfo(result)?.isNewUser) {
            await deleteUser(result.user).catch(() => undefined);
        }
        return tid;
    } finally {
        await signOut(auth).catch(() => undefined);
        await deleteApp(app).catch(() => undefined);
    }
}

function decodeTid(jwt: string): string {
    try {
        const payload = JSON.parse(atob(jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        return typeof payload?.tid === "string" ? payload.tid.toLowerCase() : "";
    } catch {
        return "";
    }
}
